<template>
  <div data-test="quantity-stepper" class="inline-flex items-center gap-2">
    <button
      type="button"
      data-test="decrease"
      :data-glyph="showTrash ? 'trash' : 'minus'"
      :aria-label="decreaseLabel"
      :disabled="decreaseDisabled"
      class="transition-all flex-shrink-0 w-[38px] h-[38px] rounded-full border border-gray-brand-400 bg-white text-gray-brand-700 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-brand-800"
      :class="decreaseDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-brand-200'"
      @click="decrease"
    >
      <TrashIcon v-if="showTrash" class="w-4 h-4" />
      <MinusIcon v-else class="w-4 h-4" />
    </button>

    <!-- A typed box rather than a readout: reaching 20 of something should not cost 19 taps. Same
         control V1 already gave the per-option quantities on the old inline card. -->
    <input
      data-test="value"
      type="text"
      inputmode="numeric"
      aria-label="Quantity"
      :value="draft"
      :disabled="disabled"
      class="flex-shrink-0 w-[52px] h-[38px] px-2 rounded-md border border-gray-brand-400 bg-white text-center font-inter font-semibold text-sm tabular-nums text-gray-brand-900 focus:outline-none focus:ring-1 focus:ring-brand-500"
      :class="{ 'opacity-50': disabled }"
      @input="onInput"
      @change="commit"
      @blur="commit"
      @keydown.enter.prevent="commit"
    />

    <button
      type="button"
      data-test="increase"
      aria-label="Increase quantity"
      :disabled="increaseDisabled"
      class="transition-all flex-shrink-0 w-[38px] h-[38px] rounded-full border border-brand-600 bg-brand-500 text-white flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-brand-800"
      :class="increaseDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-brand-600'"
      @click="increase"
    >
      <PlusIcon class="w-4 h-4" />
    </button>
  </div>
</template>

<script>
import { PlusIcon, MinusIcon, TrashIcon } from '@heroicons/vue/solid'
import { snapQuantity } from '@/models/productViewModel'

export default {
  name: 'QuantityStepper',

  components: { PlusIcon, MinusIcon, TrashIcon },

  props: {
    modelValue: {
      type: Number,
      required: true
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: null
    },
    step: {
      type: Number,
      default: 1
    },
    // The smallest quantity the product can actually be ordered in - the backend's
    // starting_quantity floor. Distinct from `min`, which is 0 for a line being edited so that the
    // line can be removed: 0 stays reachable, but nothing between 1 and the floor does, because
    // the backend would silently raise it.
    floor: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showTrashAtMin: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],

  data() {
    return { draft: String(this.modelValue) }
  },

  watch: {
    modelValue(value) {
      this.draft = String(value)
    }
  },

  computed: {
    effectiveMax() {
      return this.max === null || this.max === undefined ? Infinity : this.max
    },
    atMin() {
      return this.modelValue <= this.min
    },
    atMax() {
      return this.modelValue >= this.effectiveMax
    },
    decreaseDisabled() {
      return this.disabled || this.atMin
    },
    increaseDisabled() {
      return this.disabled || this.atMax
    },
    showTrash() {
      if (!this.showTrashAtMin) return false
      const next = this.modelValue - this.step
      return next <= this.min || next < this.floor
    },
    decreaseLabel() {
      return this.showTrash ? 'Remove item' : 'Decrease quantity'
    }
  },

  methods: {
    clamp(value) {
      return Math.min(Math.max(value, this.min), this.effectiveMax)
    },

    // Every value this control emits is one the cart can actually hold. `snapQuantity` is the
    // backend's own floor-then-round-up rule, so the customer never sits on a quantity that the
    // POST quietly turns into a larger one - which would leave the modal's running total quoting
    // a price for an order that was never placed.
    snap(value) {
      return snapQuantity(value, { step: this.step, floor: this.floor })
    },

    onInput(event) {
      // Digits only, so a stray keystroke cannot leave the box showing something unparseable.
      const digits = event.target.value.replace(/\D/g, '')
      if (event.target.value !== digits) event.target.value = digits
      this.draft = digits
    },

    commit() {
      const parsed = parseInt(this.draft, 10)
      // An empty or unreadable box snaps back to what it was, rather than silently reading as the
      // minimum and quietly changing the customer's order.
      if (Number.isNaN(parsed)) {
        this.draft = String(this.modelValue)
        return
      }
      const next = this.clamp(this.snap(parsed))
      this.draft = String(next)
      this.emitValue(next)
    },
    decrease() {
      if (this.decreaseDisabled) return
      const next = this.modelValue - this.step
      // Stepping down out of the orderable range goes all the way to `min` rather than to the
      // floor, so the last press on an edited line removes it instead of doing nothing.
      this.emitValue(next < this.floor ? this.min : this.clamp(this.snap(next)))
    },
    increase() {
      if (this.increaseDisabled) return
      this.emitValue(this.clamp(this.snap(this.modelValue + this.step)))
    },
    emitValue(value) {
      if (value === this.modelValue) return
      this.$emit('update:modelValue', value)
    }
  }
}
</script>
